import { useFormContext, type FieldValues } from "react-hook-form";
import RadioButton from "@medoma/components/RadioButton/RadioButton";
import RadioButtons from "@medoma/components/RadioButton/RadioButtons";
import { useEffect } from "react";
import { useLingui } from "@lingui/react/macro";
import {
  durationOptions,
  durationDictionary,
  durationOptionSchema,
} from "./durationModels";
import { TemplateCustomDuration } from "./TemplateCustomDuration";
import {
  useFieldPathWithPrefix,
  useFieldErrorWithPrefix,
} from "@/forms/FormFieldPathPrefixContext";
import { useIsInFinalValidationPhase } from "@/pages/template-manager/TemplateFieldValidationContext";
import type {
  TemplateDurationFields,
  TemplateCustomDurationFields,
} from "@/pages/template-manager/TemplateForm/templateFormSchema";

export const TemplateDuration = () => {
  const { t } = useLingui();
  const {
    formState: { errors },
    register,
    watch,
    unregister,
  } = useFormContext<FieldValues>();

  const durationOptionFieldName =
    useFieldPathWithPrefix<TemplateDurationFields>("durationOption");
  const customDurationFieldName =
    useFieldPathWithPrefix<TemplateCustomDurationFields>("customDuration");
  const customDurationUnitFieldName =
    useFieldPathWithPrefix<TemplateCustomDurationFields>("customDurationUnit");
  const errorMessage = useFieldErrorWithPrefix<TemplateDurationFields>(
    errors,
    "durationOption",
  );
  const isInFinalValidationPhase = useIsInFinalValidationPhase();

  const selectedDuration = watch(durationOptionFieldName);
  const isCustomDuration =
    selectedDuration === durationOptionSchema.enum.custom;

  // Unregister the custom duration fields if not using custom
  useEffect(() => {
    if (!isCustomDuration) {
      unregister(customDurationFieldName);
      unregister(customDurationUnitFieldName);
    }
  }, [
    isCustomDuration,
    unregister,
    customDurationFieldName,
    customDurationUnitFieldName,
  ]);

  return (
    <>
      <RadioButtons
        legend={t`Tidsåtgång`}
        errorMessage={errorMessage}
        orientation="horizontal"
      >
        {durationOptions.map((option) => (
          <RadioButton
            key={option}
            label={{ text: t(durationDictionary[option].displayText) }}
            visualStyle="framed"
            value={option}
            {...register(durationOptionFieldName, {
              required: isInFinalValidationPhase
                ? {
                    value: true,
                    message: t`Välj tidsåtgång`,
                  }
                : undefined,
            })}
          />
        ))}
      </RadioButtons>
      {isCustomDuration ? <TemplateCustomDuration /> : null}
    </>
  );
};
